import React, { useState, useRef, useEffect } from 'react';
import {
  Type,
  Bold,
  Italic,
  Underline,
  Strikethrough,
  AlignLeft,
  AlignCenter,
  AlignRight,
  AlignJustify,
  Copy,
  Trash2,
  ArrowUp,
  ArrowDown,
  ArrowLeft as ArrowLeftIcon,
  ArrowRight as ArrowRightIcon,
  Layers,
  Sparkles,
  Square,
  Circle,
  Minus,
  ArrowRight,
  Triangle,
  Palette,
  Sigma,
  Table as TableIcon,
  Edit3,
  X,
  Move,
  Plus,
} from 'lucide-react';
import {
  DocumentModel,
  DocumentBlock,
  ShapeType,
  ShapeBlock,
  FormulaBlock,
  ImageBlock,
  TableBlock,
} from '../ast/document.types';

export interface ManualEditToolbarProps {
  document: DocumentModel;
  selectedBlockId: string | null;
  selectedSectionId: string | null;
  onSelectBlock: (blockId: string | null, sectionId: string | null) => void;
  onUpdateBlockData: (sectionId: string, blockId: string, updates: any) => void;
  onRemoveBlock: (sectionId: string, blockId: string) => void;
  onDuplicateBlock: (sectionId: string, blockId: string) => void;
  onReorderBlockLayer: (
    sectionId: string,
    blockId: string,
    action: 'bringToFront' | 'sendToBack' | 'bringForward' | 'sendBackward'
  ) => void;
  onNudgeBlock: (sectionId: string, blockId: string, dx: number, dy: number) => void;
  onInsertShape: (sectionId: string, shapeType: ShapeType) => void;
  onInsertBlock: (sectionId: string, blockType: 'text' | 'formula' | 'table' | 'image' | 'shape') => void;
  onOpenFormulaModal?: (formulaBlock: FormulaBlock, sectionId: string) => void;
  onOpenTableModal?: (tableBlock: TableBlock, sectionId: string) => void;
}

const FONT_FAMILIES = [
  { label: 'Default Serif / Times', value: 'Times New Roman, serif' },
  { label: 'Academic Sans / Arial', value: 'Arial, Helvetica, sans-serif' },
  { label: 'Computer Modern / LaTeX', value: 'KaTeX_Main, "Computer Modern", serif' },
  { label: 'Modern Sans / Inter', value: 'Inter, system-ui, sans-serif' },
  { label: 'Clean / Roboto', value: 'Roboto, sans-serif' },
  { label: 'Formal / Georgia', value: 'Georgia, serif' },
  { label: 'Monospace / Courier', value: 'Courier New, monospace' },
];

const FONT_SIZES = [8, 9, 10, 10.5, 11, 12, 13, 14, 16, 18, 20, 24, 28, 32, 36];
const LINE_SPACINGS = [1.0, 1.15, 1.25, 1.5, 1.75, 2.0];

const PRESET_COLORS = [
  '#000000',
  '#1f2937',
  '#4b5563',
  '#1e3a8a',
  '#2563eb',
  '#059669',
  '#dc2626',
  '#d97706',
  '#7c3aed',
  '#ffffff',
];

const PRESET_HIGHLIGHTS = [
  'transparent',
  '#fef08a', // Yellow
  '#bbf7d0', // Green
  '#bfdbfe', // Blue
  '#fbcfe8', // Pink
  '#fed7aa', // Orange
  '#f3f4f6', // Light gray
];

export const ManualEditToolbar: React.FC<ManualEditToolbarProps> = ({
  document: doc,
  selectedBlockId,
  selectedSectionId,
  onSelectBlock,
  onUpdateBlockData,
  onRemoveBlock,
  onDuplicateBlock,
  onReorderBlockLayer,
  onNudgeBlock,
  onInsertShape,
  onInsertBlock,
  onOpenFormulaModal,
  onOpenTableModal,
}) => {
  // Find currently selected block and its section
  let activeBlock: DocumentBlock | null = null;
  let activeSectionId: string | null = selectedSectionId;

  if (selectedBlockId) {
    for (const sec of doc.sections) {
      const found = sec.blocks.find((b) => b.id === selectedBlockId);
      if (found) {
        activeBlock = found;
        activeSectionId = sec.id;
        break;
      }
    }
  }

  const effectiveSectionId = activeSectionId || doc.sections[0]?.id || '';

  // Dropdown states
  const [isShapeDropdownOpen, setIsShapeDropdownOpen] = useState(false);
  const [isColorPickerOpen, setIsColorPickerOpen] = useState(false);
  const [isHighlightPickerOpen, setIsHighlightPickerOpen] = useState(false);
  const [isLayerMenuOpen, setIsLayerMenuOpen] = useState(false);

  const shapeMenuRef = useRef<HTMLDivElement>(null);
  const colorMenuRef = useRef<HTMLDivElement>(null);
  const highlightMenuRef = useRef<HTMLDivElement>(null);
  const layerMenuRef = useRef<HTMLDivElement>(null);

  // Close dropdowns on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (shapeMenuRef.current && !shapeMenuRef.current.contains(e.target as Node)) {
        setIsShapeDropdownOpen(false);
      }
      if (colorMenuRef.current && !colorMenuRef.current.contains(e.target as Node)) {
        setIsColorPickerOpen(false);
      }
      if (highlightMenuRef.current && !highlightMenuRef.current.contains(e.target as Node)) {
        setIsHighlightPickerOpen(false);
      }
      if (layerMenuRef.current && !layerMenuRef.current.contains(e.target as Node)) {
        setIsLayerMenuOpen(false);
      }
    };
    window.addEventListener('mousedown', handleClickOutside);
    return () => window.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Update text formatting helper
  const handleUpdateTextFormatting = (formattingUpdates: any) => {
    if (!activeBlock || activeBlock.type !== 'text' || !activeSectionId) return;
    const currentFormatting = activeBlock.data.formatting || {};
    onUpdateBlockData(activeSectionId, activeBlock.id, {
      formatting: { ...currentFormatting, ...formattingUpdates },
    });
  };

  // Alignment helpers (e.g. Center on page, Align Left, Right, Center)
  const handleAlign = (alignment: 'left' | 'center' | 'right' | 'justify') => {
    if (!activeBlock || !activeSectionId) return;
    if (activeBlock.type === 'text') {
      handleUpdateTextFormatting({ alignment, offsetX: 0 });
    } else {
      onUpdateBlockData(activeSectionId, activeBlock.id, { alignment, offsetX: 0 });
    }
  };

  return (
    <>
      {/* =========================================================================
          1. TOP HORIZONTAL FORMATTING & CONTEXTUAL BAR
          ========================================================================= */}
      <div className="manual-edit-topbar" data-manual-toolbar="top">
        {/* Left Status / Context Indicator */}
        <div className="manual-edit-topbar-left">
          {activeBlock ? (
            <div className="manual-edit-tag">
              <span className="manual-edit-tag-dot" />
              <span className="manual-edit-tag-label">
                {activeBlock.type.toUpperCase()}
                {activeBlock.type === 'shape' ? ` (${(activeBlock as ShapeBlock).data.shapeType})` : ''}
              </span>
            </div>
          ) : (
            <div className="manual-edit-helper">
              <Sparkles size={13} style={{ color: 'var(--primary-500)' }} />
              <span>Select any element in preview to format or arrange</span>
            </div>
          )}
        </div>

        {/* Center Contextual Controls */}
        <div className="manual-edit-topbar-center">
          {activeBlock?.type === 'text' && (
            /* ================= TEXT EDITING CONTROLS ================= */
            <div className="manual-edit-group">
              {/* Font Family Selector */}
              <select
                className="form-select text-xs manual-edit-select"
                style={{ width: '135px' }}
                value={activeBlock.data.formatting?.fontFamily || ''}
                onChange={(e) => handleUpdateTextFormatting({ fontFamily: e.target.value })}
                title="Font Family"
              >
                {FONT_FAMILIES.map((f) => (
                  <option key={f.value} value={f.value}>
                    {f.label}
                  </option>
                ))}
              </select>

              {/* Font Size Stepper */}
              <select
                className="form-select text-xs manual-edit-select"
                style={{ width: '60px' }}
                value={activeBlock.data.formatting?.fontSizePt || 11}
                onChange={(e) => handleUpdateTextFormatting({ fontSizePt: parseFloat(e.target.value) })}
                title="Font Size (pt)"
              >
                {FONT_SIZES.map((sz) => (
                  <option key={sz} value={sz}>
                    {sz}pt
                  </option>
                ))}
              </select>

              <div className="manual-edit-divider" />

              {/* Bold */}
              <button
                type="button"
                className={`icon-btn ${activeBlock.data.formatting?.bold ? 'active' : ''}`}
                style={{ width: 28, height: 28 }}
                onClick={() => handleUpdateTextFormatting({ bold: !activeBlock?.data.formatting?.bold })}
                title="Bold (Ctrl+B)"
              >
                <Bold size={13} />
              </button>

              {/* Italic */}
              <button
                type="button"
                className={`icon-btn ${activeBlock.data.formatting?.italic ? 'active' : ''}`}
                style={{ width: 28, height: 28 }}
                onClick={() => handleUpdateTextFormatting({ italic: !activeBlock?.data.formatting?.italic })}
                title="Italic (Ctrl+I)"
              >
                <Italic size={13} />
              </button>

              {/* Underline */}
              <button
                type="button"
                className={`icon-btn ${activeBlock.data.formatting?.underline ? 'active' : ''}`}
                style={{ width: 28, height: 28 }}
                onClick={() => handleUpdateTextFormatting({ underline: !activeBlock?.data.formatting?.underline })}
                title="Underline (Ctrl+U)"
              >
                <Underline size={13} />
              </button>

              {/* Strikethrough */}
              <button
                type="button"
                className={`icon-btn ${activeBlock.data.formatting?.strikethrough ? 'active' : ''}`}
                style={{ width: 28, height: 28 }}
                onClick={() => handleUpdateTextFormatting({ strikethrough: !activeBlock?.data.formatting?.strikethrough })}
                title="Strikethrough"
              >
                <Strikethrough size={13} />
              </button>

              {/* Subscript */}
              <button
                type="button"
                className={`icon-btn ${activeBlock.data.formatting?.subscript ? 'active' : ''}`}
                style={{ width: 28, height: 28, fontSize: '10px', fontWeight: 700 }}
                onClick={() =>
                  handleUpdateTextFormatting({
                    subscript: !activeBlock?.data.formatting?.subscript,
                    superscript: false,
                  })
                }
                title="Subscript (X₂)"
              >
                X₂
              </button>

              {/* Superscript */}
              <button
                type="button"
                className={`icon-btn ${activeBlock.data.formatting?.superscript ? 'active' : ''}`}
                style={{ width: 28, height: 28, fontSize: '10px', fontWeight: 700 }}
                onClick={() =>
                  handleUpdateTextFormatting({
                    superscript: !activeBlock?.data.formatting?.superscript,
                    subscript: false,
                  })
                }
                title="Superscript (X²)"
              >
                X²
              </button>

              <div className="manual-edit-divider" />

              {/* Text Color Picker */}
              <div ref={colorMenuRef} style={{ position: 'relative' }}>
                <button
                  type="button"
                  className="icon-btn"
                  style={{
                    width: 28,
                    height: 28,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                  onClick={() => setIsColorPickerOpen(!isColorPickerOpen)}
                  title="Text Color"
                >
                  <span style={{ fontSize: '11px', fontWeight: 700, lineHeight: 1 }}>A</span>
                  <span
                    style={{
                      width: 12,
                      height: 3,
                      backgroundColor: activeBlock.data.formatting?.color || '#000000',
                      borderRadius: 1,
                    }}
                  />
                </button>

                {isColorPickerOpen && (
                  <div className="manual-edit-palette-popup" style={{ width: '150px' }}>
                    {PRESET_COLORS.map((c) => (
                      <button
                        key={c}
                        type="button"
                        className="manual-edit-color-swatch"
                        style={{ backgroundColor: c }}
                        onClick={() => {
                          handleUpdateTextFormatting({ color: c });
                          setIsColorPickerOpen(false);
                        }}
                      />
                    ))}
                  </div>
                )}
              </div>

              {/* Highlight Color Picker */}
              <div ref={highlightMenuRef} style={{ position: 'relative' }}>
                <button
                  type="button"
                  className="icon-btn"
                  style={{ width: 28, height: 28 }}
                  onClick={() => setIsHighlightPickerOpen(!isHighlightPickerOpen)}
                  title="Highlight / Background Color"
                >
                  <Palette size={13} style={{ color: activeBlock.data.formatting?.backgroundColor || 'inherit' }} />
                </button>

                {isHighlightPickerOpen && (
                  <div className="manual-edit-palette-popup" style={{ width: '130px', gridTemplateColumns: 'repeat(4, 1fr)' }}>
                    {PRESET_HIGHLIGHTS.map((c) => (
                      <button
                        key={c}
                        type="button"
                        className="manual-edit-color-swatch"
                        style={{
                          backgroundColor: c === 'transparent' ? '#ffffff' : c,
                        }}
                        onClick={() => {
                          handleUpdateTextFormatting({ backgroundColor: c });
                          setIsHighlightPickerOpen(false);
                        }}
                      >
                        {c === 'transparent' ? 'Ø' : ''}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div className="manual-edit-divider" />

              {/* Alignments */}
              <button
                type="button"
                className={`icon-btn ${
                  activeBlock.data.formatting?.alignment === 'left' || !activeBlock.data.formatting?.alignment ? 'active' : ''
                }`}
                style={{ width: 28, height: 28 }}
                onClick={() => handleAlign('left')}
                title="Align Left"
              >
                <AlignLeft size={13} />
              </button>
              <button
                type="button"
                className={`icon-btn ${activeBlock.data.formatting?.alignment === 'center' ? 'active' : ''}`}
                style={{ width: 28, height: 28 }}
                onClick={() => handleAlign('center')}
                title="Align Center"
              >
                <AlignCenter size={13} />
              </button>
              <button
                type="button"
                className={`icon-btn ${activeBlock.data.formatting?.alignment === 'right' ? 'active' : ''}`}
                style={{ width: 28, height: 28 }}
                onClick={() => handleAlign('right')}
                title="Align Right"
              >
                <AlignRight size={13} />
              </button>
              <button
                type="button"
                className={`icon-btn ${activeBlock.data.formatting?.alignment === 'justify' ? 'active' : ''}`}
                style={{ width: 28, height: 28 }}
                onClick={() => handleAlign('justify')}
                title="Justify"
              >
                <AlignJustify size={13} />
              </button>

              <div className="manual-edit-divider" />

              {/* Line Spacing */}
              <select
                className="form-select text-xs manual-edit-select"
                style={{ width: '64px' }}
                value={activeBlock.data.formatting?.lineHeightMultiplier || 1.6}
                onChange={(e) => handleUpdateTextFormatting({ lineHeightMultiplier: parseFloat(e.target.value) })}
                title="Line Spacing"
              >
                {LINE_SPACINGS.map((ls) => (
                  <option key={ls} value={ls}>
                    {ls}x
                  </option>
                ))}
              </select>
            </div>
          )}

          {activeBlock?.type === 'shape' && (
            /* ================= SHAPE EDITING CONTROLS ================= */
            <div className="manual-edit-group">
              {/* Shape Type Switcher */}
              <select
                className="form-select text-xs manual-edit-select"
                style={{ width: '115px' }}
                value={(activeBlock as ShapeBlock).data.shapeType || 'rectangle'}
                onChange={(e) => onUpdateBlockData(activeSectionId!, activeBlock!.id, { shapeType: e.target.value })}
                title="Shape Type"
              >
                <option value="rectangle">Rectangle</option>
                <option value="rounded_rectangle">Rounded Box</option>
                <option value="circle">Circle</option>
                <option value="ellipse">Ellipse</option>
                <option value="line">Line</option>
                <option value="arrow">Arrow</option>
                <option value="triangle">Triangle</option>
                <option value="divider">Divider</option>
              </select>

              <div className="manual-edit-divider" />

              {/* Fill Color Picker */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>Fill:</span>
                <input
                  type="color"
                  className="manual-edit-color-input"
                  value={
                    (activeBlock as ShapeBlock).data.fill === 'transparent'
                      ? '#ffffff'
                      : (activeBlock as ShapeBlock).data.fill || '#f0f4f8'
                  }
                  onChange={(e) => onUpdateBlockData(activeSectionId!, activeBlock!.id, { fill: e.target.value })}
                  title="Shape Fill Color"
                />
              </div>

              {/* Stroke / Border Color Picker */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>Border:</span>
                <input
                  type="color"
                  className="manual-edit-color-input"
                  value={(activeBlock as ShapeBlock).data.stroke || '#1e3a8a'}
                  onChange={(e) => onUpdateBlockData(activeSectionId!, activeBlock!.id, { stroke: e.target.value })}
                  title="Shape Border Color"
                />
              </div>

              <div className="manual-edit-divider" />

              {/* Stroke Width Stepper */}
              <select
                className="form-select text-xs manual-edit-select"
                style={{ width: '64px' }}
                value={(activeBlock as ShapeBlock).data.strokeWidth || 1.5}
                onChange={(e) => onUpdateBlockData(activeSectionId!, activeBlock!.id, { strokeWidth: parseFloat(e.target.value) })}
                title="Border Width"
              >
                <option value="0.5">0.5px</option>
                <option value="1">1.0px</option>
                <option value="1.5">1.5px</option>
                <option value="2">2.0px</option>
                <option value="3">3.0px</option>
                <option value="4">4.0px</option>
              </select>

              {/* Stroke Style */}
              <select
                className="form-select text-xs manual-edit-select"
                style={{ width: '70px' }}
                value={(activeBlock as ShapeBlock).data.strokeStyle || 'solid'}
                onChange={(e) => onUpdateBlockData(activeSectionId!, activeBlock!.id, { strokeStyle: e.target.value })}
                title="Border Style"
              >
                <option value="solid">Solid</option>
                <option value="dashed">Dashed</option>
                <option value="dotted">Dotted</option>
              </select>

              <div className="manual-edit-divider" />

              {/* Width / Height Stepper Controls */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <span style={{ fontSize: '10px', color: 'var(--text-muted)' }}>W:</span>
                <input
                  type="number"
                  className="form-input text-xs manual-edit-input-number"
                  style={{ width: '52px' }}
                  value={(activeBlock as ShapeBlock).data.width || 160}
                  step="10"
                  min="10"
                  max="600"
                  onChange={(e) => onUpdateBlockData(activeSectionId!, activeBlock!.id, { width: parseInt(e.target.value) || 10 })}
                  title="Shape Width (px)"
                />
                <span style={{ fontSize: '10px', color: 'var(--text-muted)' }}>H:</span>
                <input
                  type="number"
                  className="form-input text-xs manual-edit-input-number"
                  style={{ width: '52px' }}
                  value={(activeBlock as ShapeBlock).data.height || 80}
                  step="5"
                  min="2"
                  max="600"
                  onChange={(e) => onUpdateBlockData(activeSectionId!, activeBlock!.id, { height: parseInt(e.target.value) || 2 })}
                  title="Shape Height (px)"
                />
              </div>

              <div className="manual-edit-divider" />

              {/* Alignment */}
              <button type="button" className="icon-btn" style={{ width: 28, height: 28 }} onClick={() => handleAlign('left')} title="Align Left">
                <AlignLeft size={13} />
              </button>
              <button type="button" className="icon-btn" style={{ width: 28, height: 28 }} onClick={() => handleAlign('center')} title="Align Center">
                <AlignCenter size={13} />
              </button>
              <button type="button" className="icon-btn" style={{ width: 28, height: 28 }} onClick={() => handleAlign('right')} title="Align Right">
                <AlignRight size={13} />
              </button>
            </div>
          )}

          {activeBlock?.type === 'formula' && (
            /* ================= FORMULA EDITING CONTROLS ================= */
            <div className="manual-edit-group">
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                style={{ fontSize: '11px', padding: '4px 10px', gap: 6 }}
                onClick={() => onOpenFormulaModal && onOpenFormulaModal(activeBlock as FormulaBlock, activeSectionId!)}
                title="Edit LaTeX Equation"
              >
                <Edit3 size={13} />
                <span>Edit LaTeX Equation</span>
              </button>

              <button
                type="button"
                className={`btn btn-ghost btn-sm ${(activeBlock as FormulaBlock).data.displayMode ? 'active' : ''}`}
                style={{ fontSize: '11px', padding: '4px 8px' }}
                onClick={() =>
                  onUpdateBlockData(activeSectionId!, activeBlock!.id, {
                    displayMode: !(activeBlock as FormulaBlock).data.displayMode,
                  })
                }
                title="Toggle Display Mode (Block vs Inline)"
              >
                {(activeBlock as FormulaBlock).data.displayMode ? 'Block (Centered)' : 'Inline'}
              </button>

              <div className="manual-edit-divider" />

              <button type="button" className="icon-btn" style={{ width: 28, height: 28 }} onClick={() => handleAlign('left')} title="Align Left">
                <AlignLeft size={13} />
              </button>
              <button type="button" className="icon-btn" style={{ width: 28, height: 28 }} onClick={() => handleAlign('center')} title="Align Center">
                <AlignCenter size={13} />
              </button>
              <button type="button" className="icon-btn" style={{ width: 28, height: 28 }} onClick={() => handleAlign('right')} title="Align Right">
                <AlignRight size={13} />
              </button>
            </div>
          )}

          {activeBlock?.type === 'table' && (
            /* ================= TABLE EDITING CONTROLS ================= */
            <div className="manual-edit-group">
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                style={{ fontSize: '11px', padding: '4px 10px', gap: 6 }}
                onClick={() => onOpenTableModal && onOpenTableModal(activeBlock as TableBlock, activeSectionId!)}
                title="Edit Table Grid & Data"
              >
                <Edit3 size={13} />
                <span>Edit Table Cells</span>
              </button>

              <select
                className="form-select text-xs manual-edit-select"
                style={{ width: '95px' }}
                value={(activeBlock as TableBlock).data.borderStyle || 'academic'}
                onChange={(e) => onUpdateBlockData(activeSectionId!, activeBlock!.id, { borderStyle: e.target.value })}
                title="Table Border Style"
              >
                <option value="academic">Academic</option>
                <option value="grid">Grid</option>
                <option value="minimal">Minimal</option>
                <option value="none">No Border</option>
              </select>
            </div>
          )}

          {activeBlock?.type === 'image' && (
            /* ================= IMAGE EDITING CONTROLS ================= */
            <div className="manual-edit-group">
              <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>Width:</span>
              <input
                type="range"
                min="20"
                max="100"
                value={(activeBlock as ImageBlock).data.widthPercent || 80}
                onChange={(e) => onUpdateBlockData(activeSectionId!, activeBlock!.id, { widthPercent: parseInt(e.target.value) })}
                style={{ width: '80px', cursor: 'pointer' }}
                title="Image Width %"
              />
              <span style={{ fontSize: '11px', minWidth: '32px' }}>{(activeBlock as ImageBlock).data.widthPercent || 80}%</span>

              <div className="manual-edit-divider" />

              <button type="button" className="icon-btn" style={{ width: 28, height: 28 }} onClick={() => handleAlign('left')} title="Align Left">
                <AlignLeft size={13} />
              </button>
              <button type="button" className="icon-btn" style={{ width: 28, height: 28 }} onClick={() => handleAlign('center')} title="Align Center">
                <AlignCenter size={13} />
              </button>
              <button type="button" className="icon-btn" style={{ width: 28, height: 28 }} onClick={() => handleAlign('right')} title="Align Right">
                <AlignRight size={13} />
              </button>
            </div>
          )}
        </div>

        {/* Right Deselect Button if an element is selected */}
        {activeBlock && (
          <div className="manual-edit-topbar-right">
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              style={{ fontSize: '11px', padding: '3px 8px', gap: 4 }}
              onClick={() => onSelectBlock(null, null)}
              title="Deselect element (Esc)"
            >
              <X size={12} />
              <span>Done</span>
            </button>
          </div>
        )}
      </div>

      {/* =========================================================================
          2. LEFT VERTICAL FLOATING PALETTE (INSERT & CREATION TOOLS)
          ========================================================================= */}
      <div className="manual-edit-left-palette" data-manual-toolbar="left">
        <div className="manual-edit-dock-header" title="Insert Elements">
          <Plus size={14} style={{ color: 'var(--primary-500)' }} />
          <span className="manual-edit-dock-title">INSERT</span>
        </div>

        <div className="manual-edit-dock-body">
          {/* Add Text */}
          <button
            type="button"
            className="manual-edit-dock-btn"
            onClick={() => onInsertBlock(effectiveSectionId, 'text')}
            title="Add Text Box / Block"
          >
            <Type size={16} />
            <span className="manual-edit-dock-btn-label">Text</span>
          </button>

          {/* Add Formula */}
          <button
            type="button"
            className="manual-edit-dock-btn"
            onClick={() => onInsertBlock(effectiveSectionId, 'formula')}
            title="Add LaTeX Equation / Formula"
          >
            <Sigma size={16} />
            <span className="manual-edit-dock-btn-label">Formula</span>
          </button>

          {/* Add Table */}
          <button
            type="button"
            className="manual-edit-dock-btn"
            onClick={() => onInsertBlock(effectiveSectionId, 'table')}
            title="Add Academic Table"
          >
            <TableIcon size={16} />
            <span className="manual-edit-dock-btn-label">Table</span>
          </button>

          <div className="manual-edit-dock-divider" />

          {/* Insert Shape (with Flyout Submenu) */}
          <div ref={shapeMenuRef} style={{ position: 'relative' }}>
            <button
              type="button"
              className={`manual-edit-dock-btn ${isShapeDropdownOpen ? 'active' : ''}`}
              onClick={() => setIsShapeDropdownOpen(!isShapeDropdownOpen)}
              title="Insert Academic Geometric Shapes"
            >
              <Square size={16} />
              <span className="manual-edit-dock-btn-label">Shapes</span>
            </button>

            {isShapeDropdownOpen && (
              <div className="manual-edit-left-flyout">
                <div className="manual-edit-flyout-title">Geometric Shapes</div>
                <div className="manual-edit-flyout-grid">
                  {[
                    { type: 'rectangle', label: 'Rectangle', icon: <Square size={14} /> },
                    { type: 'rounded_rectangle', label: 'Rounded Box', icon: <Square size={14} style={{ borderRadius: 3 }} /> },
                    { type: 'circle', label: 'Circle', icon: <Circle size={14} /> },
                    { type: 'ellipse', label: 'Ellipse', icon: <Circle size={14} style={{ transform: 'scaleX(1.3)' }} /> },
                    { type: 'line', label: 'Line', icon: <Minus size={14} /> },
                    { type: 'arrow', label: 'Arrow', icon: <ArrowRight size={14} /> },
                    { type: 'triangle', label: 'Triangle', icon: <Triangle size={14} /> },
                    { type: 'divider', label: 'Divider', icon: <Minus size={14} style={{ borderBottom: '1px solid currentColor' }} /> },
                  ].map((s) => (
                    <button
                      key={s.type}
                      type="button"
                      className="manual-edit-flyout-item"
                      onClick={() => {
                        onInsertShape(effectiveSectionId, s.type as ShapeType);
                        setIsShapeDropdownOpen(false);
                      }}
                      title={`Insert ${s.label}`}
                    >
                      {s.icon}
                      <span>{s.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* =========================================================================
          3. RIGHT VERTICAL FLOATING INSPECTOR (NUDGE, LAYERING & ARRANGE TOOLS)
          ========================================================================= */}
      <div className={`manual-edit-right-inspector ${!activeBlock ? 'disabled-view' : ''}`} data-manual-toolbar="right">
        <div className="manual-edit-dock-header" title="Inspector & Arrange">
          <Move size={13} style={{ color: activeBlock ? 'var(--primary-500)' : 'var(--text-muted)' }} />
          <span className="manual-edit-dock-title">ARRANGE</span>
        </div>

        <div className="manual-edit-dock-body">
          {/* Directional Nudge D-Pad Cluster */}
          <div className="manual-edit-dpad-container" title="Nudge position (5px)">
            <div className="manual-edit-dpad-row">
              <button
                type="button"
                className="manual-edit-dpad-btn"
                disabled={!activeBlock}
                onClick={() => activeBlock && activeSectionId && onNudgeBlock(activeSectionId, activeBlock.id, 0, -5)}
                title="Nudge Up (5px)"
              >
                <ArrowUp size={13} />
              </button>
            </div>
            <div className="manual-edit-dpad-row">
              <button
                type="button"
                className="manual-edit-dpad-btn"
                disabled={!activeBlock}
                onClick={() => activeBlock && activeSectionId && onNudgeBlock(activeSectionId, activeBlock.id, -5, 0)}
                title="Nudge Left (5px)"
              >
                <ArrowLeftIcon size={13} />
              </button>
              <div className="manual-edit-dpad-center-dot" />
              <button
                type="button"
                className="manual-edit-dpad-btn"
                disabled={!activeBlock}
                onClick={() => activeBlock && activeSectionId && onNudgeBlock(activeSectionId, activeBlock.id, 5, 0)}
                title="Nudge Right (5px)"
              >
                <ArrowRightIcon size={13} />
              </button>
            </div>
            <div className="manual-edit-dpad-row">
              <button
                type="button"
                className="manual-edit-dpad-btn"
                disabled={!activeBlock}
                onClick={() => activeBlock && activeSectionId && onNudgeBlock(activeSectionId, activeBlock.id, 0, 5)}
                title="Nudge Down (5px)"
              >
                <ArrowDown size={13} />
              </button>
            </div>
          </div>

          <div className="manual-edit-dock-divider" />

          {/* Layer Ordering Dropdown */}
          <div ref={layerMenuRef} style={{ position: 'relative', width: '100%' }}>
            <button
              type="button"
              className={`manual-edit-dock-btn ${isLayerMenuOpen ? 'active' : ''}`}
              disabled={!activeBlock}
              onClick={() => setIsLayerMenuOpen(!isLayerMenuOpen)}
              title="Layer Ordering (Front/Back/Forward/Backward)"
            >
              <Layers size={16} />
              <span className="manual-edit-dock-btn-label">Layers</span>
            </button>

            {isLayerMenuOpen && activeBlock && (
              <div className="manual-edit-right-flyout">
                <div className="manual-edit-flyout-title">Layer Order</div>
                <button
                  type="button"
                  className="manual-edit-flyout-item"
                  onClick={() => {
                    onReorderBlockLayer(activeSectionId!, activeBlock!.id, 'bringToFront');
                    setIsLayerMenuOpen(false);
                  }}
                >
                  <ArrowUp size={13} />
                  <span>Bring to Front</span>
                </button>
                <button
                  type="button"
                  className="manual-edit-flyout-item"
                  onClick={() => {
                    onReorderBlockLayer(activeSectionId!, activeBlock!.id, 'bringForward');
                    setIsLayerMenuOpen(false);
                  }}
                >
                  <ArrowUp size={12} style={{ opacity: 0.7 }} />
                  <span>Bring Forward</span>
                </button>
                <button
                  type="button"
                  className="manual-edit-flyout-item"
                  onClick={() => {
                    onReorderBlockLayer(activeSectionId!, activeBlock!.id, 'sendBackward');
                    setIsLayerMenuOpen(false);
                  }}
                >
                  <ArrowDown size={12} style={{ opacity: 0.7 }} />
                  <span>Send Backward</span>
                </button>
                <button
                  type="button"
                  className="manual-edit-flyout-item"
                  onClick={() => {
                    onReorderBlockLayer(activeSectionId!, activeBlock!.id, 'sendToBack');
                    setIsLayerMenuOpen(false);
                  }}
                >
                  <ArrowDown size={13} />
                  <span>Send to Back</span>
                </button>
              </div>
            )}
          </div>

          <div className="manual-edit-dock-divider" />

          {/* Duplicate Block */}
          <button
            type="button"
            className="manual-edit-dock-btn"
            disabled={!activeBlock}
            onClick={() => activeBlock && activeSectionId && onDuplicateBlock(activeSectionId, activeBlock.id)}
            title="Duplicate Selected Element (Ctrl+D)"
          >
            <Copy size={16} />
            <span className="manual-edit-dock-btn-label">Duplicate</span>
          </button>

          {/* Delete Block */}
          <button
            type="button"
            className="manual-edit-dock-btn text-danger"
            disabled={!activeBlock}
            onClick={() => activeBlock && activeSectionId && onRemoveBlock(activeSectionId, activeBlock.id)}
            title="Delete Selected Element (Del / Backspace)"
          >
            <Trash2 size={16} />
            <span className="manual-edit-dock-btn-label">Delete</span>
          </button>
        </div>
      </div>
    </>
  );
};
